import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import type { BatchContractAction } from '../types/parcels';
import { isParcelCancelled, isParcelOpen } from './parcelStatusPredicates';

/** Quantas parcelas o modal lista antes de resumir o resto em "+N outras". */
export const BATCH_SAMPLE_SIZE = 3;

/** Conjunto de parcelas que uma ação em lote vai atingir. */
export interface BatchTargets {
  /** Ids enviados no corpo da request — todos, não apenas os da amostra. */
  readonly ids: readonly number[];
  /** Quantas parcelas serão atingidas. */
  readonly count: number;
  /** Primeiras parcelas do conjunto, para o operador reconhecer o que vai. */
  readonly sample: readonly IndividualPaymentParcelViewModel[];
  /** Quantas ficaram fora da amostra. */
  readonly hiddenCount: number;
  /** Soma do valor previsto (`total`) das parcelas atingidas. */
  readonly totalValue: number;
}

/** Predicado de elegibilidade de cada ação em lote. */
const PREDICATE_BY_ACTION = {
  cancelOpenParcels: isParcelOpen,
  openCancelledParcels: isParcelCancelled,
} as const satisfies Record<
  BatchContractAction,
  (parcel: IndividualPaymentParcelViewModel) => boolean
>;

/**
 * Deriva o conjunto de parcelas que uma ação em lote vai atingir.
 *
 * Os endpoints `parcels/cancel-open` e `parcels/open-cancelled` recebem um corpo
 * `number[]`. Como o endpoint de parcelas devolve a coleção completa (sem
 * paginação), o conjunto sai da lista que a tela já tem — nenhuma seleção por
 * linha é necessária.
 *
 * Parcelas sem `id` são descartadas: sem id não há o que enviar.
 *
 * @param parcels - Parcelas do contrato, como vieram do endpoint.
 * @param action - Ação em lote escolhida.
 * @returns Ids, contagem, amostra, quantas ficaram fora e a soma dos valores.
 *
 * @example
 * ```ts
 * const { ids, count, totalValue } = deriveBatchTargets(parcels, 'cancelOpenParcels');
 * ```
 */
export function deriveBatchTargets(
  parcels: readonly IndividualPaymentParcelViewModel[],
  action: BatchContractAction,
): BatchTargets {
  const predicate = PREDICATE_BY_ACTION[action];
  const targets = parcels.filter(parcel => parcel.id !== undefined && predicate(parcel));

  return {
    ids: targets.map(parcel => parcel.id as number),
    count: targets.length,
    sample: targets.slice(0, BATCH_SAMPLE_SIZE),
    hiddenCount: Math.max(targets.length - BATCH_SAMPLE_SIZE, 0),
    totalValue: targets.reduce((sum, parcel) => sum + (parcel.total ?? 0), 0),
  };
}
